import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill, VerifiedBadge, Stars, LoadingScreen } from "@/components/common";
import { Star, Eye } from "lucide-react";

const ALL = "all";

export default function AdminAgencies() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [fCity, setFCity] = useState(ALL);
  const [fVerif, setFVerif] = useState(ALL);
  const [fPkg, setFPkg] = useState(ALL);
  const [fListing, setFListing] = useState(ALL);
  const [fFeatured, setFFeatured] = useState(ALL);
  useEffect(() => { api.get("/admin/agencies").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  const cities = useMemo(() => [...new Set((rows || []).map((a) => a.head_office?.city).filter(Boolean))].sort(), [rows]);
  const packages = useMemo(() => [...new Set((rows || []).map((a) => a.subscription_plan).filter(Boolean))].sort(), [rows]);

  const filtered = (rows || []).filter((a) =>
    a.name.toLowerCase().includes(q.toLowerCase()) &&
    (fCity === ALL || a.head_office?.city === fCity) &&
    (fVerif === ALL || a.verification_status === fVerif) &&
    (fPkg === ALL || a.subscription_plan === fPkg) &&
    (fListing === ALL || (fListing === "listed" ? a.is_listed : !a.is_listed)) &&
    (fFeatured === ALL || (fFeatured === "yes" ? a.featured : !a.featured))
  );

  const FilterSelect = ({ value, onChange, placeholder, options, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[150px]" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent><SelectItem value={ALL}>{placeholder}</SelectItem>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <DashboardShell menu={adminMenu} title="Agencies">
      <PageHeader title="Manage Agencies" subtitle="Verify, score, manage packages and control marketplace visibility." />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input placeholder="Search agencies…" value={q} onChange={(e) => setQ(e.target.value)} className="w-56 h-9" data-testid="agency-search" />
        <FilterSelect value={fCity} onChange={setFCity} placeholder="All Cities" testid="filter-city" options={cities.map((c) => ({ v: c, l: c }))} />
        <FilterSelect value={fVerif} onChange={setFVerif} placeholder="All Verification" testid="filter-verification" options={["VERIFIED", "UNDER_REVIEW", "PENDING", "REJECTED", "EXPIRED"].map((s) => ({ v: s, l: s.replace(/_/g, " ") }))} />
        <FilterSelect value={fPkg} onChange={setFPkg} placeholder="All Packages" testid="filter-package" options={packages.map((p) => ({ v: p, l: p }))} />
        <FilterSelect value={fListing} onChange={setFListing} placeholder="All Listing" testid="filter-listing" options={[{ v: "listed", l: "Listed" }, { v: "hidden", l: "Not Listed" }]} />
        <FilterSelect value={fFeatured} onChange={setFFeatured} placeholder="All Featured" testid="filter-featured" options={[{ v: "yes", l: "Featured" }, { v: "no", l: "Not Featured" }]} />
      </div>

      {!rows ? <LoadingScreen /> : (
        <div className="bg-white border border-navy-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[1040px]">
            <thead className="bg-navy-50 text-navy-500 text-xs uppercase"><tr>
              <th className="text-left p-3">Agency</th><th className="text-left p-3">City</th>
              <th className="p-3">Admin Score</th><th className="p-3">Customer Rating</th><th className="p-3">Reviews</th>
              <th className="p-3">Verification</th><th className="p-3">Package</th><th className="p-3">Subscription</th>
              <th className="p-3">Listing</th><th className="p-3">Featured</th><th className="p-3">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className="border-t border-navy-100 hover:bg-navy-50/50" data-testid={`admin-agency-${a.id}`}>
                  <td className="p-3 font-medium text-navy-900">{a.name}</td>
                  <td className="p-3 text-navy-600">{a.head_office?.city || "—"}</td>
                  <td className="p-3 text-center"><span className="inline-flex items-center gap-1 text-navy-800" data-testid={`score-${a.id}`}><Star size={12} className="text-amber-500 fill-amber-500" /> {Number(a.admin_verification_score || 0).toFixed(1)}</span></td>
                  <td className="p-3 text-center"><Stars value={a.rating} size={11} /></td>
                  <td className="p-3 text-center text-navy-600">{a.reviews_count ?? 0}</td>
                  <td className="p-3 text-center"><VerifiedBadge verified={a.verified} /></td>
                  <td className="p-3 text-center text-navy-700 font-medium">{a.subscription_plan || "—"}</td>
                  <td className="p-3 text-center"><StatusPill status={a.subscription_status || "ACTIVE"} /></td>
                  <td className="p-3 text-center"><StatusPill status={a.listing_state} /></td>
                  <td className="p-3 text-center">{a.featured ? <Star size={15} className="inline text-amber-500 fill-amber-500" data-testid={`featured-${a.id}`} /> : <span className="text-navy-300">—</span>}</td>
                  <td className="p-3 text-center">
                    <Button size="sm" variant="outline" className="border-navy-300" onClick={() => navigate(`/admin/agencies/${a.id}`)} data-testid={`view-details-${a.id}`}><Eye size={14} className="mr-1" /> Manage</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="p-8 text-center text-navy-400 text-sm">No agencies match these filters.</div>}
        </div>
      )}
    </DashboardShell>
  );
}
